import { HowTo } from '../types';

/**
 * Admin How Tos.
 *
 * A small audience — one or two people per setting — but the tasks are rare,
 * consequential and often done once at setup and then forgotten, which is
 * exactly when a guide is worth having. Every one of these is gated on the
 * Admin role, so they never appear in a teacher's list.
 */
export const adminHowTos: HowTo[] = [
  {
    key: 'admin.attendance-in-template',
    version: 1,
    section: 'Admin',
    title: 'Add attendance stats and charts to a report template',
    summary:
      "Put attendance figures, and the charts that go with them, into the reports this setting produces.",
    youNeed: [
      'The Admin role',
      'A report template to change — or make a new one',
    ],
    keywords: [
      'template', 'templates', 'report', 'reports', 'attendance', 'stats',
      'statistics', 'figures', 'charts', 'chart', 'graph', 'graphs', 'add',
      'section', 'sections', 'timeframe', 'weekly', 'monthly', 'greyed out',
      'disabled', 'build', 'customise', 'customize',
    ],
    routes: ['/admin'],
    audience: 'admin',
    requiredRole: 'Admin',
    steps: [
      {
        text: 'Open **Admin Dashboard** from the menu, then the **Report Templates** tab.',
        // Most specific first: the tab strip while you are on Admin, the menu
        // item that gets you there when you are not.
        target: ['admin.tabs', 'nav.admin'],
        route: '/admin?tab=reportTemplates',
        navigateOnly: true,
      },
      {
        text: 'Open the template you want to change, or click **New template**. Editing the stock **Weekly Report** makes this setting its own copy — the original is left alone.',
        target: 'rtb.new',
      },
      {
        text: 'Check the **Timeframe** first. It decides which charts are available: a monthly report can show trends across weeks, a daily or weekly one cannot. Choosing it later means revisiting your chart picks.',
        target: 'rtb.timeframe',
      },
      {
        text: 'Under the sections, click **Attendance** to add an attendance section. A template can hold one alongside its posts.',
        target: 'rtb.add-attendance',
      },
      {
        text: 'Tick **Stats**, **Charts**, or both. Each one opens a list underneath so you choose exactly which figures and which charts appear — not all of them by default.',
        target: 'rtb.attendance-toggles',
      },
      {
        text: "A greyed-out chart cannot say anything over this timeframe. Hover it to see which it suits, then change the **Timeframe**.",
        target: 'rtb.charts-picker',
      },
      {
        text: 'Watch the preview beside it. It fills with sample data, so you can see the shape of the report before any real child is in it.',
        target: 'rtb.preview',
      },
      {
        text: 'Click **Save template**. New reports use it straight away; reports already generated keep the layout they were made with until they are regenerated.',
        target: 'rtb.save',
      },
    ],
    related: ['reports.generate-pdf'],
  },
  {
    key: 'admin.add-child',
    version: 1,
    section: 'Admin',
    title: 'Add a child',
    summary: 'Create a child profile so posts, sessions and attendance can be recorded against them.',
    youNeed: ['The Admin role'],
    keywords: [
      'child', 'children', 'add', 'new', 'create', 'profile', 'pupil',
      'student', 'starter', 'joined', 'enrol', 'enroll', 'admission',
      'avatar', 'photo', 'colour', 'color', 'initials',
    ],
    routes: ['/admin'],
    audience: 'admin',
    requiredRole: 'Admin',
    steps: [
      {
        text: 'Open **Admin Dashboard** from the menu, then the **Child Profiles** tab.',
        target: ['admin.tabs', 'nav.admin'],
        route: '/admin?tab=children',
        navigateOnly: true,
      },
      {
        text: 'Click **New Child**.',
        target: 'admin.new-child',
      },
      {
        text: 'Fill in the details. The colour you choose is how this child is identified across the schedule and attendance grids, so pick something distinct from the children they share sessions with.',
      },
      {
        text: 'Add a profile photo if you have one. It appears wherever the child is listed, and it is what staff recognise a name by on a busy grid.',
      },
      {
        // Two labels, one button: Create child for a new one, Save changes
        // when editing. Anyone in this guide is creating.
        text: 'Click **Create child**. If it is refused because the plan is full, the number of children is capped by your subscription — a billing question rather than a setting here.',
        target: 'admin.child-save',
      },
      {
        text: 'The child appears in the rail on every page. Sessions can now be scheduled and posts written for them.',
      },
    ],
    related: ['admin.mark-as-left', 'posts.create-post'],
  },
  {
    key: 'admin.invite-user',
    version: 1,
    section: 'Admin',
    title: 'Invite a new user and set what they can do',
    summary:
      'Add a member of staff, choose their roles, and set which children they can see.',
    youNeed: ['The Admin role', "The person's email address"],
    keywords: [
      'user', 'users', 'staff', 'invite', 'invitation', 'add', 'new', 'account',
      'role', 'roles', 'permission', 'permissions', 'access', 'editor',
      'reporting', 'admin', 'external', 'parent', 'whitelist', 'resend',
      'password', 'cannot see', 'no access',
    ],
    routes: ['/admin'],
    audience: 'admin',
    requiredRole: 'Admin',
    steps: [
      {
        text: 'Open **Admin Dashboard** from the menu, then the **Users** tab.',
        target: ['admin.tabs', 'nav.admin'],
        route: '/admin?tab=staff',
        navigateOnly: true,
      },
      {
        text: 'Click **New User** and enter their name and email address. The invitation goes to that address, so check it now rather than after.',
        target: 'admin.new-user',
      },
      {
        text: 'Choose their **Roles**. **Editor** writes posts, **Reporting** reviews and publishes them and produces reports, **Admin** reaches this screen. Roles add up — most staff need Editor alone.',
        target: 'admin.roles',
      },
      {
        text: 'Under **Access**, choose **Staff** or **External**. An external member sees only the children you list for them, can edit only their own posts, and cannot also be an Admin.',
        target: 'admin.access',
      },
      {
        text: 'Set the **child whitelist**. It applies to everyone, not just external users: it decides which children they can view, and add or edit posts for.',
        target: 'admin.whitelist',
      },
      {
        text: 'An empty list means opposite things for the two kinds of member: a member of staff reaches **every** child, an external user sees **none**. Fill it in before they sign in.',
        target: 'admin.whitelist',
      },
      {
        // Create user for a new one, Save changes when editing — the reader
        // of this guide is creating.
        text: 'Click **Create user**. They receive an invitation and set their own password, then a PIN for this device. If it never arrives, open them again and resend it.',
        target: 'admin.user-save',
      },
    ],
    related: ['admin.add-child'],
  },
  {
    key: 'admin.mark-as-left',
    version: 1,
    section: 'Admin',
    title: 'Mark a child as left',
    summary:
      'Take a child who has moved on out of the day-to-day lists, without deleting what was recorded about them.',
    youNeed: ['The Admin role'],
    keywords: [
      'left', 'leaver', 'leavers', 'leaving', 'moved on', 'departed', 'remove',
      'archive', 'deactivate', 'inactive', 'delete', 'child', 'children',
      'ex-pupil', 'no longer',
    ],
    routes: ['/admin'],
    audience: 'admin',
    requiredRole: 'Admin',
    steps: [
      {
        text: 'Open **Admin Dashboard** from the menu, then the **Child Profiles** tab.',
        target: ['admin.tabs', 'nav.admin'],
        route: '/admin?tab=children',
        navigateOnly: true,
      },
      {
        text: 'Open the child.',
      },
      {
        text: 'Click **Mark as left…** and give the date they left.',
        target: 'admin.mark-as-left',
      },
      {
        text: 'Nothing is deleted. Their posts, sessions and attendance stay as they were; this only takes them out of the day-to-day lists.',
      },
      {
        text: 'They move to the **Leavers** tab, which only appears once there is somebody on it. Undo it from there if it was a mistake, or if they come back.',
        target: 'admin.tabs',
      },
    ],
    related: ['admin.add-child'],
  },
  {
    key: 'admin.grid-interval',
    version: 1,
    section: 'Admin',
    title: "Change the schedule grid's time step",
    summary:
      'Set how finely sessions can be dragged and resized — the snap the schedule grid uses.',
    youNeed: ['The Admin role'],
    keywords: [
      'interval', 'grid', 'step', 'snap', 'snapping', 'minutes', '15', '30',
      'time', 'times', 'schedule', 'timetable', 'resize', 'drag', 'precise',
      'granularity', 'setting', 'settings', 'organisation',
    ],
    routes: ['/admin'],
    audience: 'admin',
    requiredRole: 'Admin',
    steps: [
      {
        text: 'Open **Admin Dashboard** from the menu, then the **Organisation** tab.',
        target: ['admin.tabs', 'nav.admin'],
        route: '/admin?tab=organisation',
        navigateOnly: true,
      },
      {
        text: 'Click **Schedule & Attendance** in the rail on the left.',
        target: 'admin.org-rail-schedule',
      },
      {
        text: 'Set **Schedule grid interval** — the step every session snaps to when dragged or resized. 15 minutes suits most settings; 5 gives finer control on a busier grid.',
        target: 'admin.grid-interval',
      },
      {
        text: 'Click **Save Changes**. The schedule grid redraws to match. Sessions already saved keep their times, whatever the new step is — nothing is rounded to fit.',
        target: 'admin.org-save',
      },
    ],
    related: ['schedule.change-session-times'],
  },
  {
    key: 'admin.org-logo',
    version: 1,
    section: 'Admin',
    title: 'Put your logo and colours on your reports',
    summary:
      'Upload your logo once. It heads every report from then on, and the reports take their colour from it.',
    youNeed: ['The Admin role', 'Your logo as a PNG, JPG or WebP, under 5 MB'],
    keywords: [
      'logo', 'brand', 'branding', 'colour', 'color', 'header', 'letterhead',
      'report', 'reports', 'pdf', 'organisation', 'organization', 'company',
      'image', 'picture', 'upload', 'background', 'transparent', 'white',
      'theme', 'look', 'appearance', 'setting', 'settings',
    ],
    routes: ['/admin'],
    audience: 'admin',
    requiredRole: 'Admin',
    steps: [
      {
        text: 'Open **Admin Dashboard** from the menu, then the **Organisation** tab. The logo sits under **General**.',
        target: ['admin.tabs', 'nav.admin'],
        route: '/admin?tab=organisation',
      },
      {
        text: 'Click **Upload logo** — or **Replace logo** if one is already there. PNG, JPG or WebP, up to 5 MB. Anything else is refused, and so is a file over the limit.',
        target: 'admin.org-logo',
      },
      {
        text: 'It says **Cleaning background & reading colour…** for a moment. The background is stripped for you, so there is no need to prepare a transparent version first.',
        target: 'admin.org-logo',
      },
      {
        text: 'Compare against **Original** using the **Background** switch, and choose it if the clean-up has eaten part of the logo. The choice disappears once you save.',
        target: 'admin.logo-background',
      },
      {
        text: 'The **Brand colour** is read from the logo and colours your reports. Adjust it if the one it picked is wrong.',
        target: 'admin.brand-colour',
      },
      {
        text: 'Click **Save Changes**. Nothing is uploaded until you do — leaving the page first loses both the logo and the colour it read.',
        target: 'admin.org-save',
      },
      {
        text: 'The logo now heads every report, and the ring around the preview shows the colour they will use.',
        target: 'admin.org-logo',
      },
    ],
    related: ['reports.generate-pdf'],
  },
  {
    key: 'admin.restore-leaver',
    version: 1,
    section: 'Admin',
    title: 'Bring back a child who left',
    summary:
      'Undo a child marked as left — at any point before the date their record is due to be deleted.',
    youNeed: ['The Admin role'],
    keywords: [
      'restore', 'restored', 'undo', 'back', 'returned', 'returning',
      'came back', 'rejoined', 'mistake', 'wrong child', 'left', 'leaver',
      'leavers', 'reinstate', 'reactivate', 'deleted', 'deletion',
      'child', 'children', 'profile',
    ],
    routes: ['/admin'],
    audience: 'admin',
    requiredRole: 'Admin',
    steps: [
      {
        text: 'Open **Admin Dashboard** from the menu, then the **Leavers** tab. If there is no such tab, nobody has been marked as left — it only appears when it has something to show.',
        target: ['admin.tabs', 'nav.admin'],
        route: '/admin?tab=leavers',
      },
      {
        text: 'Find the child. Each one shows a **Deletion date**: until that day their record is only hidden, and nothing about it has changed. On it, an Admin can delete the record for good.',
      },
      {
        text: 'Click **Restore**. That is the whole of the undo — the leaving date and the scheduled deletion are both cleared.',
        target: 'admin.leaver-restore',
      },
      {
        text: 'They come back **inactive**, not straight into the day, so nothing reappears on a grid until you say so.',
      },
      {
        text: 'If the child really is coming back, make them active again on the **Child Profiles** tab. Their old posts, sessions and attendance were never touched, so they are all still there.',
        route: '/admin?tab=children',
      },
      {
        text: 'Past the deletion date there is nothing to restore — the record has gone rather than been hidden. The date on each row is how much time is left to change your mind.',
      },
    ],
    related: ['admin.mark-as-left'],
  },
  {
    key: 'admin.change-roles',
    version: 1,
    section: 'Admin',
    title: "Change what someone can do",
    summary:
      'Add or remove a member of staff\'s roles — and why the Admin box is sometimes greyed out.',
    youNeed: ['The Admin role'],
    keywords: [
      'role', 'roles', 'permission', 'permissions', 'access', 'rights',
      'change', 'edit', 'add', 'remove', 'revoke', 'grant', 'promote',
      'demote', 'editor', 'reporting', 'admin', 'greyed', 'greyed out',
      'disabled', 'locked', 'last admin', 'only admin', 'cannot remove',
      'user', 'users', 'staff', 'member',
    ],
    routes: ['/admin'],
    audience: 'admin',
    requiredRole: 'Admin',
    steps: [
      {
        text: 'Open **Admin Dashboard** from the menu, then the **Users** tab.',
        target: ['admin.tabs', 'nav.admin'],
        route: '/admin?tab=staff',
        navigateOnly: true,
      },
      {
        text: 'Click the person in the list to open their record.',
      },
      {
        text: 'Tick or untick under **Roles**. **Editor** writes posts, **Reporting** reviews and publishes them and produces reports, **Admin** reaches this screen. Roles add up.',
        target: 'admin.roles',
      },
      {
        text: 'Giving a role is never blocked. Taking **Admin** away sometimes is — the box greys out, with the reason beside it.',
        target: 'admin.roles',
      },
      {
        text: 'It says **you can\'t remove your own Admin role** on your own record, so nobody locks themselves out by accident.',
      },
      {
        text: 'It says **the only Admin in this organisation** when they are the last one standing. Give Admin to somebody else first, and it can then be taken from them.',
      },
      {
        text: 'The same rule guards the record itself: the only Admin cannot be deactivated either.',
      },
      {
        text: '**External** is not in this list. It sits under **Access** above, as a choice between **Staff** and **External** — and it is refused while they hold Admin, since the two are mutually exclusive.',
        target: 'admin.access',
      },
      {
        text: 'Click **Save changes**. Roles apply the next time that person signs in or switches to their tile.',
        target: 'admin.user-save',
      },
    ],
    related: ['admin.invite-user'],
  },
];
